import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import AddOrganizationalUnit from "./AddOrganizationalUnit";
import AddUserDialog from "./AddUserDialog";
import React, { useState, useRef, useEffect } from "react";
import RenderOnRole from "../../components/RenderOnRole";
import ButtonWithIcon from "../../components/Button";
import ListAltIcon from "@mui/icons-material/ListAlt";
import UserService from "../../services/UserService";
import Labels from "../../labels/Labels";

async function fetchOrganizationalUnits() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/subdepartment/all`
  );
  return response;
}

async function fetchOrganizationalUnitDetails(subDepartmentId) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/subdepartment/${subDepartmentId}/custom-fields`
  );
  return response;
}

async function fetchCustomFieldsConfig(parentCompanyId) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/subdepartment/custom-fields/${parentCompanyId}`
  );
  return response;
}

async function fetchSubDepartmentUsers(subDepartmentId) {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/subdepartment/${subDepartmentId}/users`
  );
  return response;
}

export default function ManageOrganizationalUnit() {
  const addUserFormikRef = useRef(null);
  const [previousValues, setPreviousValues] = useState({});
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    subDepartmentId: null,
  });
  const [addUserDialog, setAddUserDialog] = useState({
    open: false,
    subDepartmentId: null,
  });
  const [viewUsersDialog, setViewUsersDialog] = useState({
    open: false,
    subDepartmentId: null,
  });
  const [deleteUserDialog, setDeleteUserDialog] = useState({
    open: false,
    username: null,
    subDepartmentId: null,
  });
  const [editDialog, setEditDialog] = useState({
    open: false,
    row: null,
  });
  const [editFormValues, setEditFormValues] = useState({
    name: "",
    description: "",
    customFields: {},
  });

  const queryClient = useQueryClient();

  const updateOrganizationalUnitMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().put(
        window.API_URL + `/subdepartment/${values.id}`,
        {
          name: values.row.name,
          description: values.row.description,
          customFields: values.row.customFields,
        }
      );
    },
    {
      onSuccess: (data, value) => {
        setPreviousValues((prevPreviousValues) => {
          const newPreviousValues = { ...prevPreviousValues };
          delete newPreviousValues[value.id];
          return newPreviousValues;
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error updating the organizational unit",
        });
      },
    }
  );

  const deleteOrganizationalUnitMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/subdepartment/${values.id}`
      );
    },
    {
      onSuccess: (dataResponse, value) => {
        data.data = data.data.filter((row) => row.id !== value.id);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error deleting the organizational unit",
        });
      },
    }
  );

  const handleEditCellChange = (id, field, oldValue, newValue) => {
    if (newValue !== undefined && oldValue !== newValue) {
      setPreviousValues((prevPreviousValues) => ({
        ...prevPreviousValues,
        [id]: { ...prevPreviousValues[id], [field]: oldValue },
      }));
    }
  };

  const clearEditedRow = (params) => {
    if (previousValues[params.id].name) {
      params.row.name = previousValues[params.id].name;
    }
    if (previousValues[params.id].description) {
      params.row.description = previousValues[params.id].description;
    }
    setPreviousValues((prevPreviousValues) => {
      const newPreviousValues = { ...prevPreviousValues };
      delete newPreviousValues[params.id];
      return newPreviousValues;
    });
  };

  const confirmSubDepartmentMutation = useMutation(
    (idSubDepartment) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/subdepartment/${idSubDepartment}/confirm`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["organizationalUnits"] });
        setFeedback({
          open: true,
          type: "success",
          message: "The request was verified successfully",
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const cancelSubDepartmentMutation = useMutation(
    (idSubDepartment) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/subdepartment/${idSubDepartment}/cancel`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["organizationalUnits"] });
        setFeedback({
          open: true,
          type: "success",
          message: "The request was verified succesfully",
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const createSubDepartmentUserMutation = useMutation(
    ({ subDepartmentId, userData }) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/subdepartment/${subDepartmentId}/users`,
        {
          idSubDepartment: subDepartmentId,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
        }
      );
    },
    {
      onSuccess: (response) => {
        // Check if the response indicates the user already exists
        if (response?.data?.status === "ALREADY_EXISTS") {
          setFeedback({
            open: true,
            type: "warning",
            message: Labels.getLabel(
              "manageOrganizationalUnit.userAlreadyExists",
              "User already exists in this organizational unit"
            ),
          });
          return;
        }

        queryClient.invalidateQueries({ queryKey: ["subdepartmentUsers"] });
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "manageOrganizationalUnit.userAddedSuccess",
            "User added successfully to organizational unit"
          ),
        });
        handleCloseAddUserDialog();

        // Reset formik form
        if (addUserFormikRef.current) {
          addUserFormikRef.current.resetForm();
        }
      },
      onError: (err) => {
        // Handle server-side validation errors
        if (err?.response?.data?.validationErrors) {
          const serverErrors = {};
          err.response.data.validationErrors.forEach((error) => {
            serverErrors[error.field] = error.message;
          });

          // Set errors in formik
          if (addUserFormikRef.current) {
            addUserFormikRef.current.setErrors(serverErrors);
          }

          setFeedback({
            open: true,
            type: "error",
            message:
              err.response.data.validationMessage ||
              "Validation failed. Please check the errors and try again.",
          });
          return;
        }

        const msg = err?.response?.data || err.message;
        setFeedback({
          open: true,
          type: "error",
          message:
            typeof msg === "string"
              ? msg
              : "Error adding user to organizational unit",
        });
      },
    }
  );

  const deleteSubDepartmentUserMutation = useMutation(
    ({ subDepartmentId, username }) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL +
          `/subdepartment/${subDepartmentId}/users/${encodeURIComponent(username)}`
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["subdepartmentUsers"] });
        setFeedback({
          open: true,
          type: "success",
          message: Labels.getLabel(
            "manageOrganizationalUnit.userDeletedSuccess",
            "User removed successfully from organizational unit"
          ),
        });
        setDeleteUserDialog({
          open: false,
          username: null,
          subDepartmentId: null,
        });
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.description ||
          err?.response?.data ||
          err.message;
        setFeedback({
          open: true,
          type: "error",
          message:
            typeof msg === "string"
              ? msg
              : "Error removing user from organizational unit",
        });
      },
    }
  );

  const onVerifyButtonClick = (e, params) => {
    confirmSubDepartmentMutation.mutate(params.id);
  };

  const onCancelButtonClick = (e, params) => {
    cancelSubDepartmentMutation.mutate(params.id);
  };

  const onViewDetailsClick = (e, params) => {
    setDetailDialog({
      open: true,
      subDepartmentId: params.id,
    });
  };

  const handleCloseDetailDialog = () => {
    setDetailDialog({
      open: false,
      subDepartmentId: null,
    });
  };

  const onAddUserClick = (e, params) => {
    setAddUserDialog({
      open: true,
      subDepartmentId: params.id,
    });
  };

  const handleCloseAddUserDialog = () => {
    setAddUserDialog({
      open: false,
      subDepartmentId: null,
    });
    // Reset formik form
    if (addUserFormikRef.current) {
      addUserFormikRef.current.resetForm();
    }
  };

  const handleAddUserSubmit = (values) => {
    createSubDepartmentUserMutation.mutate({
      subDepartmentId: addUserDialog.subDepartmentId,
      userData: values,
    });
  };

  const onViewUsersClick = (e, params) => {
    setViewUsersDialog({
      open: true,
      subDepartmentId: params.id,
    });
  };

  const handleCloseViewUsersDialog = () => {
    setViewUsersDialog({
      open: false,
      subDepartmentId: null,
    });
  };

  const handleDeleteUserClick = (username) => {
    setDeleteUserDialog({
      open: true,
      username: username,
      subDepartmentId: viewUsersDialog.subDepartmentId,
    });
  };

  const handleCloseDeleteUserDialog = () => {
    setDeleteUserDialog({
      open: false,
      username: null,
      subDepartmentId: null,
    });
  };

  const handleConfirmDeleteUser = () => {
    deleteSubDepartmentUserMutation.mutate({
      subDepartmentId: deleteUserDialog.subDepartmentId,
      username: deleteUserDialog.username,
    });
  };

  const onEditClick = (e, params) => {
    setEditFormValues({
      name: params.row.name || "",
      description: params.row.description || "",
      customFields: {},
    });
    setEditDialog({ open: true, row: params.row });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({ open: false, row: null });
  };

  const handleSaveEdit = () => {
    updateOrganizationalUnitMutation.mutate(
      { id: editDialog.row.id, row: editFormValues },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["organizationalUnits"] });
          setFeedback({
            open: true,
            type: "success",
            message: "Organizational unit updated successfully",
          });
          handleCloseEditDialog();
        },
      }
    );
  };

  // Fetch existing custom field values for edit dialog
  const { data: editCustomFieldsData, isLoading: isLoadingEditCustomFields } =
    useQuery({
      queryKey: ["editCustomFields", editDialog.row?.id],
      queryFn: () => fetchOrganizationalUnitDetails(editDialog.row.id),
      enabled: !!editDialog.row?.id && editDialog.open,
    });

  // Fetch allowed field definitions (config) for the employer
  const { data: editCustomFieldsConfig, isLoading: isLoadingEditConfig } =
    useQuery({
      queryKey: ["editCustomFieldsConfig", editDialog.row?.parentCompanyId],
      queryFn: () => fetchCustomFieldsConfig(editDialog.row.parentCompanyId),
      enabled: !!editDialog.row?.parentCompanyId && editDialog.open,
    });

  useEffect(() => {
    if (!editDialog.open) return;
    // Build map from existing values
    const existingMap = {};
    if (editCustomFieldsData?.data?.customFields) {
      editCustomFieldsData.data.customFields.forEach((f) => {
        existingMap[f.fieldId] = f.fieldValue || "";
      });
    }
    // Add any configured fields not yet set
    if (editCustomFieldsConfig?.data?.fieldList) {
      editCustomFieldsConfig.data.fieldList.forEach((f) => {
        if (!(f.key in existingMap)) {
          existingMap[f.key] = "";
        }
      });
    }
    if (Object.keys(existingMap).length > 0) {
      setEditFormValues((prev) => ({ ...prev, customFields: existingMap }));
    }
  }, [editCustomFieldsData, editCustomFieldsConfig, editDialog.open]);

  // Fetch detail data when dialog is open
  const { data: detailData, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["organizationalUnitDetails", detailDialog.subDepartmentId],
    queryFn: () => fetchOrganizationalUnitDetails(detailDialog.subDepartmentId),
    enabled: !!detailDialog.subDepartmentId && detailDialog.open,
  });

  // Fetch users when view users dialog is open
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["subdepartmentUsers", viewUsersDialog.subDepartmentId],
    queryFn: () => fetchSubDepartmentUsers(viewUsersDialog.subDepartmentId),
    enabled: !!viewUsersDialog.subDepartmentId && viewUsersDialog.open,
  });

  const columns = [
    {
      field: "action",
      headerName: Labels.getLabel(
        "manageOrganizationalUnit.column.action",
        "Action"
      ),
      width: 150,
      renderCell: (params) => {
        return (
          <div key={"action." + params.id} style={{ display: "flex" }}>
            <RenderOnRole roles={["subdepartment-confirm"]}>
              <ButtonWithIcon
                hidden={
                  !(
                    (params.row.status === "PENDING_CONFIRMATION" ||
                      params.row.status === "CHANGE_REQUESTED") &&
                    params.row.allowApprove === "true"
                  )
                }
                onClick={onVerifyButtonClick}
                color="green"
                tooltip="Click to verify and accept the request"
                icon="confirm"
                record={params.row}
                isLoading={confirmSubDepartmentMutation.isLoading}
                disabled={cancelSubDepartmentMutation.isLoading}
              />
              <ButtonWithIcon
                hidden={
                  !(
                    (params.row.status === "PENDING_CONFIRMATION" ||
                      params.row.status === "CHANGE_REQUESTED") &&
                    params.row.allowApprove === "true"
                  )
                }
                onClick={onCancelButtonClick}
                color="red"
                tooltip="Click to cancel and close the request"
                icon="deny"
                record={params.row}
                isLoading={cancelSubDepartmentMutation.isLoading}
                disabled={confirmSubDepartmentMutation.isLoading}
              />
            </RenderOnRole>

            <IconButton
              key={"detail." + params.id}
              onClick={(e) => onViewDetailsClick(e, params)}
              color="primary"
              title="View Details"
            >
              <VisibilityIcon />
            </IconButton>

            <RenderOnRole roles={["ou-edit-attributes"]}>
              <IconButton
                key={"edit." + params.id}
                onClick={(e) => onEditClick(e, params)}
                color="secondary"
                title="Edit Organizational Unit"
              >
                <EditIcon />
              </IconButton>
            </RenderOnRole>

            <RenderOnRole roles={["config-subdepartment-users"]}>
              {params.row.status === "PROCESSED" && (
                <>
                  <IconButton
                    key={"addUser." + params.id}
                    onClick={(e) => onAddUserClick(e, params)}
                    color="success"
                    title="Add User to Organizational Unit"
                  >
                    <PersonAddIcon />
                  </IconButton>

                  <IconButton
                    key={"viewUsers." + params.id}
                    onClick={(e) => onViewUsersClick(e, params)}
                    color="info"
                    title="View Users in Organizational Unit"
                  >
                    <PeopleIcon />
                  </IconButton>
                </>
              )}
            </RenderOnRole>

            {params.id in previousValues ? (
              <IconButton
                key={"clear." + params.id}
                onClick={() => {
                  clearEditedRow(params);
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <>
                {params.row.status !== "PENDING_CONFIRMATION" &&
                  params.row.status !== "CHANGE_REQUESTED" && <></>}
              </>
            )}
          </div>
        );
      },
    },
    {
      field: "id",
      headerName: Labels.getLabel("manageOrganizationalUnit.column.id", "ID"),
      width: 70,
      hide: true,
    },
    {
      field: "parentCompanyName",
      headerName: Labels.getLabel(
        "manageOrganizationalUnit.column.parentCompanyName",
        "Employer"
      ),
      width: 200,
    },
    {
      field: "name",
      headerName: Labels.getLabel(
        "manageOrganizationalUnit.column.name",
        "Unit Name"
      ),
      width: 300,
      editable: false,
      renderCell: (params) => {
        return (
          <>
            <div
              style={{
                minWidth: "4rem",
                display: "flex",
                flexDirection: "column",
                alignContent: "left",
                alignItems: "flex-start",
                textAlign: "left",
              }}
            >
              <Typography
                fontSize="0.9rem"
                sx={{ width: "45%", flexShrink: 0 }}
              >
                {params.row?.name}
              </Typography>
              <Typography
                fontSize={12}
                sx={{
                  fontSize: "0.7rem",
                  mb: 0,
                  fontWeight: "bold",
                  color: "gray",
                }}
                gutterBottom
              >
                {params.row?.id}
              </Typography>
            </div>
          </>
        );
      },
    },
    {
      field: "description",
      headerName: Labels.getLabel(
        "manageOrganizationalUnit.column.description",
        "Description"
      ),
      width: 400,
      editable: false,
    },
    {
      field: "address",
      headerName: Labels.getLabel(
        "manageOrganizationalUnit.column.address",
        "Address"
      ),
      width: 300,
      sortable: false,
      renderCell: (params) => {
        if (!params.row?.location) {
          return null;
        }

        return (
          <>
            <div
              style={{
                minWidth: "4rem",
                display: "flex",
                flexDirection: "column",
                alignContent: "left",
                alignItems: "flex-start",
                textAlign: "left",
              }}
            >
              <Typography
                fontSize="0.9rem"
                sx={{ width: "45%", flexShrink: 0 }}
              >
                {params.row?.location?.address_line_1}{" "}
                {params.row?.location?.address_line_2 !== null &&
                params.row?.location?.address_line_2 !== undefined &&
                params.row?.location?.address_line_2 !== ""
                  ? "( " + params.row?.location?.address_line_2 + " )"
                  : ""}
              </Typography>
              <Typography
                fontSize={12}
                sx={{
                  fontSize: "0.7rem",
                  mb: 0,
                  fontWeight: "bold",
                  color: "gray",
                }}
                gutterBottom
              >
                {params.row?.location?.city}, {params.row?.location?.state}{" "}
                {params.row?.location?.zip}
              </Typography>
            </div>
          </>
        );
      },
    },
    {
      field: "status",
      headerName: Labels.getLabel(
        "manageOrganizationalUnit.column.status",
        "Status"
      ),
      width: 165,
      editable: false,
      renderCell: (params) => {
        return (
          // you will find row info in params
          <div>
            <Typography
              sx={{ display: "block", fontWeight: "bold", fontSize: 12, mb: 0 }}
              gutterBottom
            >
              {params.row.status}
            </Typography>
          </div>
        );
      },
    },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ["organizationalUnits"],
    queryFn: fetchOrganizationalUnits,
  });
  const [addUnitDlg, setAddUnitDlg] = useState(false);

  const addUnitDlgFunct = () => {
    return (
      <Dialog open={addUnitDlg} fullWidth maxWidth="md" onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageOrganizationalUnit.addUnit",
                "Add Organizational Unit"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddUnitDlg(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddOrganizationalUnit handleClose={() => setAddUnitDlg(false)} />
        </DialogContent>
      </Dialog>
    );
  };

  const detailViewDlgFunct = () => {
    return (
      <Dialog
        open={detailDialog.open}
        fullWidth
        maxWidth="md"
        onClose={handleCloseDetailDialog}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VisibilityIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageOrganizationalUnit.viewDetails",
                "Organizational Unit Details"
              )}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDetailDialog} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          {isLoadingDetails ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {Labels.getLabel(
                  "manageOrganizationalUnit.loadingDetails",
                  "Loading details..."
                )}
              </Typography>
            </Box>
          ) : detailData?.data ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {detailData.data.subDepartmentName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ID: {detailData.data.subDepartmentId}
              </Typography>

              {detailData.data.customFields &&
              detailData.data.customFields.length > 0 ? (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }}>
                    {Labels.getLabel(
                      "manageOrganizationalUnit.customFields",
                      "Custom Fields"
                    )}{" "}
                    ({detailData.data.totalFields})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {Labels.getLabel(
                              "manageOrganizationalUnit.fieldName",
                              "Field Name"
                            )}
                          </TableCell>
                          <TableCell>
                            {Labels.getLabel(
                              "manageOrganizationalUnit.fieldValue",
                              "Value"
                            )}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailData.data.customFields.map((field, index) => (
                          <TableRow key={field.fieldId || index}>
                            <TableCell component="th" scope="row">
                              {Labels.getLabel(field.fieldId, field.fieldId)}
                            </TableCell>
                            <TableCell>{field.fieldValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 3 }}
                >
                  {Labels.getLabel(
                    "manageOrganizationalUnit.noCustomFields",
                    "No custom fields available for this organizational unit."
                  )}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {Labels.getLabel(
                "manageOrganizationalUnit.errorLoadingDetails",
                "Error loading organizational unit details."
              )}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const deleteUserConfirmationDialog = () => {
    return (
      <Dialog
        open={deleteUserDialog.open}
        onClose={handleCloseDeleteUserDialog}
        maxWidth="sm"
        fullWidth
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageOrganizationalUnit.confirmDeleteTitle",
                "Confirm User Removal"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDeleteUserDialog}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <Typography>
            {Labels.getLabel(
              "manageOrganizationalUnit.confirmDeleteMessage",
              "Are you sure you want to remove this user from the organizational unit?"
            )}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, fontWeight: "bold" }}>
            {Labels.getLabel("manageOrganizationalUnit.username", "Username")}:{" "}
            {deleteUserDialog.username}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteUserDialog}
            disabled={deleteSubDepartmentUserMutation.isLoading}
          >
            {Labels.getLabel("manageOrganizationalUnit.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeleteUser}
            variant="contained"
            color="error"
            disabled={deleteSubDepartmentUserMutation.isLoading}
            startIcon={
              deleteSubDepartmentUserMutation.isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {Labels.getLabel("manageOrganizationalUnit.remove", "Remove")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const editDlgFunct = () => {
    return (
      <Dialog
        open={editDialog.open}
        fullWidth
        maxWidth="sm"
        onClose={handleCloseEditDialog}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageOrganizationalUnit.editUnit",
                "Edit Organizational Unit"
              )}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          {isLoadingEditCustomFields || isLoadingEditConfig ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {Labels.getLabel(
                  "manageOrganizationalUnit.loadingDetails",
                  "Loading details..."
                )}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                fullWidth
                label={Labels.getLabel(
                  "manageOrganizationalUnit.column.name",
                  "Unit Name"
                )}
                value={editFormValues.name}
                onChange={(e) =>
                  setEditFormValues((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                disabled={updateOrganizationalUnitMutation.isLoading}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                fullWidth
                label={Labels.getLabel(
                  "manageOrganizationalUnit.column.description",
                  "Description"
                )}
                value={editFormValues.description}
                onChange={(e) =>
                  setEditFormValues((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                disabled={updateOrganizationalUnitMutation.isLoading}
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
              />
              {Object.keys(editFormValues.customFields).length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {Labels.getLabel(
                      "manageOrganizationalUnit.customFields",
                      "Custom Fields"
                    )}
                  </Typography>
                  {Object.entries(editFormValues.customFields).map(
                    ([fieldId, fieldValue]) => {
                      const fieldConfig =
                        editCustomFieldsConfig?.data?.fieldList?.find(
                          (f) => f.key === fieldId
                        );
                      const label = fieldConfig?.label
                        ? Labels.getLabel(fieldConfig.label, fieldConfig.label)
                        : Labels.getLabel(fieldId, fieldId);
                      if (
                        fieldConfig?.type === "SELECT" &&
                        fieldConfig?.options?.length > 0
                      ) {
                        return (
                          <Autocomplete
                            key={fieldId}
                            options={fieldConfig.options}
                            getOptionLabel={(opt) =>
                              typeof opt === "string"
                                ? opt
                                : opt.description || opt.value || ""
                            }
                            value={
                              fieldConfig.options.find(
                                (o) => o.value === fieldValue
                              ) ||
                              (fieldValue
                                ? { value: fieldValue, description: fieldValue }
                                : null)
                            }
                            onChange={(_, newValue) =>
                              setEditFormValues((prev) => ({
                                ...prev,
                                customFields: {
                                  ...prev.customFields,
                                  [fieldId]: newValue?.value || "",
                                },
                              }))
                            }
                            disabled={
                              updateOrganizationalUnitMutation.isLoading
                            }
                            renderInput={(params) => (
                              <TextField {...params} fullWidth label={label} />
                            )}
                          />
                        );
                      }
                      return (
                        <TextField
                          key={fieldId}
                          fullWidth
                          label={label}
                          value={fieldValue}
                          onChange={(e) =>
                            setEditFormValues((prev) => ({
                              ...prev,
                              customFields: {
                                ...prev.customFields,
                                [fieldId]: e.target.value,
                              },
                            }))
                          }
                          disabled={updateOrganizationalUnitMutation.isLoading}
                          inputProps={{
                            maxLength: fieldConfig?.validation?.maxLength,
                          }}
                        />
                      );
                    }
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditDialog}
            disabled={updateOrganizationalUnitMutation.isLoading}
          >
            {Labels.getLabel("manageOrganizationalUnit.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="primary"
            disabled={
              updateOrganizationalUnitMutation.isLoading ||
              isLoadingEditCustomFields ||
              isLoadingEditConfig ||
              !editFormValues.name
            }
            startIcon={
              updateOrganizationalUnitMutation.isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {Labels.getLabel("manageOrganizationalUnit.save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const viewUsersDialogFunct = () => {
    return (
      <Dialog
        open={viewUsersDialog.open}
        fullWidth
        maxWidth="md"
        onClose={handleCloseViewUsersDialog}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PeopleIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageOrganizationalUnit.viewUsersTitle",
                "Users in Organizational Unit"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseViewUsersDialog}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          {isLoadingUsers ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {Labels.getLabel(
                  "manageOrganizationalUnit.loadingUsers",
                  "Loading users..."
                )}
              </Typography>
            </Box>
          ) : usersData?.data && usersData.data.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {Labels.getLabel(
                  "manageOrganizationalUnit.totalUsers",
                  "Total Users"
                )}
                : {usersData.data.length}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {Labels.getLabel(
                          "manageOrganizationalUnit.username",
                          "UserName"
                        )}
                      </TableCell>
                      <TableCell>
                        {Labels.getLabel(
                          "manageOrganizationalUnit.email",
                          "Email"
                        )}
                      </TableCell>
                      <TableCell>
                        {Labels.getLabel(
                          "manageOrganizationalUnit.firstName",
                          "First Name"
                        )}
                      </TableCell>
                      <TableCell>
                        {Labels.getLabel(
                          "manageOrganizationalUnit.lastName",
                          "Last Name"
                        )}
                      </TableCell>
                      <TableCell>
                        {Labels.getLabel(
                          "manageOrganizationalUnit.createdAt",
                          "Created At"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {Labels.getLabel(
                          "manageOrganizationalUnit.actions",
                          "Actions"
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersData.data.map((user, index) => (
                      <TableRow key={user.id || index}>
                        <TableCell>{user.username || "-"}</TableCell>
                        <TableCell>{user.email || "-"}</TableCell>
                        <TableCell>{user.firstName || "-"}</TableCell>
                        <TableCell>{user.lastName || "-"}</TableCell>
                        <TableCell>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleString()
                            : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUserClick(user.username)}
                            title="Remove user from organizational unit"
                            disabled={deleteSubDepartmentUserMutation.isLoading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, mb: 2 }}
            >
              {Labels.getLabel(
                "manageOrganizationalUnit.noUsers",
                "No users found for this organizational unit."
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewUsersDialog}>
            {Labels.getLabel("manageOrganizationalUnit.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            {Labels.getLabel("manageOrganizationalUnit.loading", "Loading...")}
          </Typography>
        </Box>
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12} sx={{ margin: "0px 0px 10px 0px" }}>
            <Box display="flex">
              <Typography variant="h5">
                {Labels.getLabel(
                  "manageOrganizationalUnit.title",
                  "Organizational Units"
                )}
              </Typography>
              <RenderOnRole roles={["create-subdepartment"]}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAddUnitDlg(true)}
                  sx={{ margin: "0px 10px 0px 10px" }}
                  disabled={
                    isLoading ||
                    confirmSubDepartmentMutation.isLoading ||
                    cancelSubDepartmentMutation.isLoading
                  }
                >
                  {Labels.getLabel(
                    "manageOrganizationalUnit.addUnit",
                    "Add Organizational Unit"
                  )}
                </Button>
              </RenderOnRole>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              key={"DataGrid.ManageOrganizationalUnit"}
              rows={data.data || []}
              columns={columns}
              loading={isLoading}
              onCellEditStop={(params, event) =>
                handleEditCellChange(
                  params.id,
                  params.field,
                  params.value,
                  event?.target?.value
                )
              }
              disableDensitySelector
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      {addUnitDlg ? addUnitDlgFunct() : ""}
      {detailDialog.open ? detailViewDlgFunct() : ""}
      {editDialog.open ? editDlgFunct() : ""}
      <AddUserDialog
        open={addUserDialog.open}
        onClose={handleCloseAddUserDialog}
        subDepartmentId={addUserDialog.subDepartmentId}
        onSubmit={handleAddUserSubmit}
        isLoading={createSubDepartmentUserMutation.isLoading}
        formikRef={addUserFormikRef}
      />
      {viewUsersDialog.open ? viewUsersDialogFunct() : ""}
      {deleteUserDialog.open ? deleteUserConfirmationDialog() : ""}

      {/* Loading backdrop for mutations */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(2px)",
        }}
        open={
          confirmSubDepartmentMutation.isLoading ||
          cancelSubDepartmentMutation.isLoading
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body1">
            {confirmSubDepartmentMutation.isLoading
              ? Labels.getLabel(
                  "manageOrganizationalUnit.confirming",
                  "Confirming request..."
                )
              : Labels.getLabel(
                  "manageOrganizationalUnit.canceling",
                  "Canceling request..."
                )}
          </Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
